/* PHOTO VIEW — which part of a photo shows in a fixed-size slot, as Bynder URL parameters (2026-09-15).
 *
 * Justin Bradford, Teams 2026-09-15: "the image itself is very tall so the builder cut it … a much bigger
 * issue if the image can not be adjusted inside of the builder." TPU_Hillcrest_Pic_Capri_Exterior_2of2 is
 * 2000×3000; the Main Image slot is 500×350; the picker asks Bynder for a centre `fill`, which kept the garage
 * and dropped the roof. Jack's call (same day): keep today's centre fill as the DEFAULT, and when a photo's
 * shape disagrees with its slot let the requester zoom the photo out (down to the whole photo, white filling
 * the rest of the frame) and drag it to choose the cut — the frame never changes size.
 *
 * MEASURED on lennar.bynder.com (2026-09-15, my own tab, the same asset), each returning a real 500×350:
 *   ?io=transform:fill,width:500,height:350                                              centre crop (today)
 *   ?io=transform:crop,x:0,y:1345,width:2000,height:1400&io=transform:fill,width:500,height:350
 *                                                                                        a chosen crop, frame full
 *   ?io=transform:extend,width:500,height:350,background:ffffff                          the whole photo on white
 *   ?io=transform:crop,x:0,y:545,width:2000,height:1910&io=transform:extend,width:500,height:350,background:ffffff
 *                                                                                        zoomed out + dragged: the
 *                                                                                        region, scaled to fit, white
 *                                                                                        around it (ink began at x=74,
 *                                                                                        expected 75)
 *   `pad`, `fit,…,background`                                                            400 — not supported
 * So a VIEW is three numbers — scale `s` (slot px per source px) and the photo's offset `ox, oy` in slot px —
 * and it is written INTO the URL. The same URL draws the canvas, ships in the email and lands on the Airtable
 * row (Main Image Info); there is no second field and nothing an email client has to interpret.
 *
 * This module is pure. The card owns the gesture; this owns the maths and the URL both ways (a URL is read back
 * into a view so a re-opened block shows where the photo was left). */

export interface Dims { w: number; h: number }
export interface View { s: number; ox: number; oy: number }

const TRANSFORM_RE = /^https?:\/\/[^/]*bynder\.com\/transform\/[^?#]+/i;

/** true for a Bynder Dynamic Asset Transformation URL (with or without parameters) */
export function isBynderTransform(url: unknown): boolean {
  return TRANSFORM_RE.test(String(url || ""));
}
/** the transform base — everything before the query */
export function bynderBase(url: unknown): string {
  return String(url || "").split("?")[0];
}

/**
 * The DAM page for the asset behind a transform URL — what the ops team opens to pull the
 * original (2026-09-15, Justin: "This is the current link that is pulling in for images
 * …/transform/4d7a3efe-…/TPU_Hillcrest_Pic_Capri_Exterior_2of2 — if we could get it to go
 * to …/media/?field=text&value=<name>…&mediaId=<id> instead that would be a huge help").
 *
 *   /transform/<uuid>/<AssetName>[?io=…]
 *     → https://<host>/media/?field=text&value=<AssetName>&filterType=add&filterKey=&viewType=grid
 *                            &mediaId=<ID>
 *
 * `value` filters the grid to the asset by name; `mediaId` opens its detail on top of that.
 * Bynder writes the media id UPPERCASE in the 8-4-4-16 shape (the hyphen between the last two
 * uuid groups is not there: 4d7a3efe-e980-4a26-952a-556b0fabef8f → 4D7A3EFE-E980-4A26-952A556B0FABEF8F),
 * copied from Justin's working link. Returns "" for anything that is not a transform URL, so
 * a caller can fall back to the URL it already has.
 */
export function bynderMediaLink(url: unknown): string {
  const m = /^(https?:\/\/[^/]*bynder\.com)\/transform\/([0-9a-f]{8})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{12})\/([^/?#]+)/i
    .exec(String(url || ""));
  if (!m) return "";
  const [, host, a, b, c, d, e] = m;
  const id = (a + "-" + b + "-" + c + "-" + d + e).toUpperCase();
  /* mediaId ALONE (2026-09-17). The link used to carry `field=text&value=<AssetName>` as well;
     Justin: "the main image … is no longer pulling the image itself, but the entire CHB library"
     — the name filter matched nothing (the row's rich-text cell mangles the underscores in
     CHB_Farmington_Rend_Streetscape) and Bynder showed the unfiltered grid. His working link is
     `/media/?viewType=grid&mediaId=<ID>`, which opens the asset with no filter to break. */
  return host + "/media/?viewType=grid&mediaId=" + id;
}
/** the asset's name as it reads in Bynder — the last path segment of a transform URL */
export function bynderAssetName(url: unknown): string {
  const m = /\/transform\/[^/]+\/([^/?#]+)/i.exec(String(url || ""));
  try { return m ? decodeURIComponent(m[1]) : ""; } catch { return m ? m[1] : ""; }
}
/** a tiny `fit` of the same asset — the cheapest way to learn the SOURCE's shape when its size is unknown */
export function bynderProbeUrl(url: unknown): string {
  const b = bynderBase(url);
  return b ? b + "?io=transform:fit,width:120,height:120" : "";
}
/** "2000x3000" ↔ {w,h}; the block stores the picked asset's size beside the photo (`<field>__nat`) */
export function parseDims(v: unknown): Dims | null {
  const m = /^(\d+)\s*[x×]\s*(\d+)$/.exec(String(v || "").trim());
  return m && +m[1] > 0 && +m[2] > 0 ? { w: +m[1], h: +m[2] } : null;
}
export function dimsKey(nat: Dims): string { return nat.w + "x" + nat.h; }

/** the scale at which the photo exactly covers the slot (today's fill) */
export function fillScale(nat: Dims, slot: Dims): number { return Math.max(slot.w / nat.w, slot.h / nat.h); }
/** the scale at which the whole photo sits inside the slot */
export function wholeScale(nat: Dims, slot: Dims): number { return Math.min(slot.w / nat.w, slot.h / nat.h); }
/** does the photo's shape disagree with the slot's by more than 3%? (when it agrees there is nothing to adjust) */
export function needsView(nat: Dims, slot: Dims): boolean {
  const a = nat.w / nat.h, b = slot.w / slot.h;
  return Math.abs(a - b) / b >= 0.03;
}
/** the default view — the centre fill, exactly what the picker's URL means */
export function fillView(nat: Dims, slot: Dims): View {
  const s = fillScale(nat, slot);
  return { s, ox: (slot.w - nat.w * s) / 2, oy: (slot.h - nat.h * s) / 2 };
}
/* ── THE FRAME IS ALWAYS FULL; ZOOM GOES IN, NOT OUT (2026-09-17) ─────────────────────────
   Justin Bradford, Teams 09-17: "they are able to zoom out below the 350px height, causing white
   to show behind the image … it should not allow them to go smaller, but should enable them to
   zoom in and move the image around as needed" (Stensul's behaviour). Jack agreed on the mock.
   So the slider's floor is the centre fill (today's default, no white ever) and its ceiling is
   ZOOM_MAX × that; drag chooses the cut at any zoom. `wholeScale` and the `extend` URL shapes
   stay exported so a URL saved under the old rule still reads back (`viewFromUrl`) — the clamp
   simply lifts it to fill the next time it is touched. */
export const ZOOM_MAX = 2;
/** the largest scale the slider reaches */
export function maxScale(nat: Dims, slot: Dims): number { return fillScale(nat, slot) * ZOOM_MAX; }
/** keep the photo inside the rules: never smaller than the fill, never past ZOOM_MAX; an axis the photo overflows can be dragged, an axis it does not is centred */
export function clampView(v: View, nat: Dims, slot: Dims): View {
  const s = Math.min(maxScale(nat, slot), Math.max(fillScale(nat, slot), v.s));
  const w = nat.w * s, h = nat.h * s;
  const ox = w <= slot.w + 1e-6 ? (slot.w - w) / 2 : Math.min(0, Math.max(slot.w - w, v.ox));
  const oy = h <= slot.h + 1e-6 ? (slot.h - h) / 2 : Math.min(0, Math.max(slot.h - h, v.oy));
  return { s, ox, oy };
}
/** zoom about the frame's centre: t = 0 fill … 1 ZOOM_MAX× */
export function zoomTo(v: View, t: number, nat: Dims, slot: Dims): View {
  const s0 = fillScale(nat, slot), s1 = maxScale(nat, slot);
  const s = s0 + (s1 - s0) * Math.min(1, Math.max(0, t));
  const cx = (slot.w / 2 - v.ox) / v.s, cy = (slot.h / 2 - v.oy) / v.s;
  return clampView({ s, ox: slot.w / 2 - cx * s, oy: slot.h / 2 - cy * s }, nat, slot);
}
/** where a view sits on the zoom slider, 0 … 1 */
export function zoomOf(v: View, nat: Dims, slot: Dims): number {
  const s0 = fillScale(nat, slot), s1 = maxScale(nat, slot);
  return s1 - s0 < 1e-9 ? 1 : Math.min(1, Math.max(0, (v.s - s0) / (s1 - s0)));
}

/** the visible window of the SOURCE, in source px, clipped to the photo */
function window_(v: View, nat: Dims, slot: Dims) {
  const x0 = Math.max(0, Math.round(-v.ox / v.s)), y0 = Math.max(0, Math.round(-v.oy / v.s));
  const x1 = Math.min(nat.w, Math.round((slot.w - v.ox) / v.s)), y1 = Math.min(nat.h, Math.round((slot.h - v.oy) / v.s));
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}

/* ── BYNDER IGNORES `x`/`y` ON A CROP; IT HONOURS `gravity` (measured 2026-09-18) ──────────────
   Jack, on the Main Image: "there seems to be a disconnect with the preview and the actual email
   they're not aligned." The card drew the dragged view; the email drew Bynder's own anchor. Measured
   on lennar.bynder.com in my own tab, distinct output sizes so no cache could alias them:
     crop,x:0,y:0,width:800,height:560 · x:2200,y:0 · x:0,y:1100 · x:2200,y:1100 (each + fill)
                                                          → four IDENTICAL images: x and y do nothing
     fill,width:500,height:350,gravity:left / right / top / bottom / center / topleft / bottomleft / bottomright
                                                          → each a different, correct anchor
     crop,width:W1,height:H1,gravity:topleft & crop,width:w,height:h,gravity:bottomright & fill
                                                          → EXACTLY the region [W1−w … W1] × [H1−h … H1]
   So a free rectangle is written as two gravity crops: the first keeps the top-left (x+w)×(y+h) of
   the source, the second keeps the bottom-right w×h of that — which is the region at (x, y). The
   09-15 header above recorded `crop,x,y` as "a chosen crop"; only its SIZE had been checked. Every
   view written since then shipped Bynder's centre (or its own anchor) while the card showed the drag.
   `viewFromUrl` still reads the old shape so a saved row opens on the view its requester chose; the
   next touch rewrites it in the working shape. */
function cropRegion(win: { x: number; y: number; w: number; h: number }): string {
  const first = "io=transform:crop,width:" + (win.x + win.w) + ",height:" + (win.y + win.h) + ",gravity:topleft";
  if (win.x === 0 && win.y === 0) return first;
  return first + "&io=transform:crop,width:" + win.w + ",height:" + win.h + ",gravity:bottomright";
}

/** THE URL FOR A VIEW. Frame full → two gravity crops + fill; otherwise crop + extend on white (legacy); the whole photo → extend alone. */
export function viewUrl(base: string, nat: Dims, slot: Dims, view: View): string {
  const v = clampView(view, nat, slot);
  const b = bynderBase(base);
  const full = nat.w * v.s >= slot.w - 0.5 && nat.h * v.s >= slot.h - 0.5;
  const win = window_(v, nat, slot);
  const centreFill = full && Math.abs(v.s - fillScale(nat, slot)) < 1e-9 && Math.abs(win.x - (nat.w - win.w) / 2) <= 1 && Math.abs(win.y - (nat.h - win.h) / 2) <= 1;
  if (centreFill) return b + "?io=transform:fill,width:" + slot.w + ",height:" + slot.h;   /* the picker's own URL, byte for byte */
  if (full) return b + "?" + cropRegion(win) + "&io=transform:fill,width:" + slot.w + ",height:" + slot.h;
  if (win.x === 0 && win.y === 0 && win.w === nat.w && win.h === nat.h) return b + "?io=transform:extend,width:" + slot.w + ",height:" + slot.h + ",background:ffffff";
  return b + "?io=transform:crop,x:" + win.x + ",y:" + win.y + ",width:" + win.w + ",height:" + win.h + "&io=transform:extend,width:" + slot.w + ",height:" + slot.h + ",background:ffffff";
}

/** THE VIEW A URL MEANS — so a re-opened block shows where the photo was left. Unknown shapes read as the centre fill. */
export function viewFromUrl(url: string, nat: Dims, slot: Dims): View {
  const q = String(url || "");
  const ext = /transform:extend,width:(\d+),height:(\d+)/i.test(q);
  const fill = /transform:fill,width:(\d+),height:(\d+)/i.test(q);
  /* the working shape: top-left (x+w)×(y+h), then bottom-right w×h (absent when x = y = 0) */
  const tl = /transform:crop,width:(\d+),height:(\d+),gravity:topleft/i.exec(q);
  if (tl && fill) {
    const br = /transform:crop,width:(\d+),height:(\d+),gravity:bottomright/i.exec(q);
    const w = br ? +br[1] : +tl[1], h = br ? +br[2] : +tl[2];
    const x = +tl[1] - w, y = +tl[2] - h;
    const s = slot.w / w;
    return clampView({ s, ox: -x * s, oy: -y * s }, nat, slot);
  }
  /* the 09-15 shape — read so a saved row opens on its chosen view; rewritten on the next touch */
  const crop = /transform:crop,x:(\d+),y:(\d+),width:(\d+),height:(\d+)/i.exec(q);
  if (crop && fill) {
    const [x, y, w] = [+crop[1], +crop[2], +crop[3]];
    const s = slot.w / w;
    return clampView({ s, ox: -x * s, oy: -y * s }, nat, slot);
  }
  if (crop && ext) {
    const [x, y, w, h] = [+crop[1], +crop[2], +crop[3], +crop[4]];
    const s = Math.min(slot.w / w, slot.h / h);
    /* extend centres the region in the frame */
    return clampView({ s, ox: (slot.w - w * s) / 2 - x * s, oy: (slot.h - h * s) / 2 - y * s }, nat, slot);
  }
  /* the whole-photo-on-white state no longer exists (2026-09-17): a URL saved that way reads as the centre fill */
  if (ext) return fillView(nat, slot);
  return fillView(nat, slot);
}

/**
 * A URL saved in the 09-15 shape (`crop,x,y,width,height` + fill/extend) rewritten into the working
 * gravity shape, so a draft or template saved before 2026-09-18 SHIPS the region its requester chose
 * rather than Bynder's anchor. Any other string comes back untouched, byte for byte. Runs at render
 * (echRegistry) on every image field — the canvas and the email both go through it.
 */
export function fixLegacyCropUrl(url: unknown): string {
  const u = String(url || "");
  if (!isBynderTransform(u) || !/transform:crop,x:\d+,y:\d+,width:\d+,height:\d+/i.test(u)) return u;
  return u.replace(/io=transform:crop,x:(\d+),y:(\d+),width:(\d+),height:(\d+)/i, (_m, x, y, w, h) =>
    cropRegion({ x: +x, y: +y, w: +w, h: +h }));
}
